import type { Card, Finish } from './collection.ts';

// Color groups, for searching by color (drawer filing itself is in drawers.ts).
export const COLOR_GROUPS = ['White', 'Blue', 'Black', 'Red', 'Green', 'Multicolor', 'Colorless', 'Lands'] as const;
export type ColorGroup = (typeof COLOR_GROUPS)[number];

const MONO: Record<string, ColorGroup> = { W: 'White', U: 'Blue', B: 'Black', R: 'Red', G: 'Green' };

/** Which drawer group a card files under. Lands go together whatever their color. */
export function colorGroup(card: Pick<Card, 'colors' | 'typeLine'>): ColorGroup {
  if (parseTypeLine(card.typeLine).types.includes('Land')) return 'Lands';
  if (card.colors.length === 0) return 'Colorless';
  if (card.colors.length > 1) return 'Multicolor';
  return MONO[card.colors[0]!]!;
}

// Type groups, used to section decks and to search by type.
// Checked in this order: a land creature counts as a creature, an artifact
// creature as a creature, an artifact land as a land.
export const TYPE_GROUPS = [
  'Creatures',
  'Lands',
  'Planeswalkers',
  'Battles',
  'Instants',
  'Sorceries',
  'Artifacts',
  'Enchantments',
  'Other',
] as const;
export type TypeGroup = (typeof TYPE_GROUPS)[number];

const TYPE_WORDS: [string, TypeGroup][] = [
  ['Creature', 'Creatures'],
  ['Land', 'Lands'],
  ['Planeswalker', 'Planeswalkers'],
  ['Battle', 'Battles'],
  ['Instant', 'Instants'],
  ['Sorcery', 'Sorceries'],
  ['Artifact', 'Artifacts'],
  ['Enchantment', 'Enchantments'],
];

const frontType = (typeLine: string) => typeLine.split(' // ')[0] ?? typeLine;

const SUPERTYPES = new Set(['Basic', 'Legendary', 'Ongoing', 'Snow', 'World', 'Elite', 'Host']);

/** Splits a type line's front face into supertypes, card types and subtypes. */
export function parseTypeLine(typeLine: string): { supertypes: string[]; types: string[]; subtypes: string[] } {
  const [left = '', right = ''] = frontType(typeLine).split(/\s+[—-]\s+/);
  const words = left.split(/\s+/).filter(Boolean);
  return {
    supertypes: words.filter((w) => SUPERTYPES.has(w)),
    types: words.filter((w) => !SUPERTYPES.has(w)),
    subtypes: right.split(/\s+/).filter(Boolean),
  };
}

export function typeGroup(typeLine: string): TypeGroup {
  // Card types only, so a subtype can never be mistaken for one.
  const { types } = parseTypeLine(typeLine);
  return TYPE_WORDS.find(([word]) => types.includes(word))?.[1] ?? 'Other';
}

/** USD for one copy in this finish, or null when Scryfall has no price. */
export function priceFor(card: Pick<Card, 'prices'>, finish: Finish): number | null {
  const raw = finish === 'foil' ? card.prices.usdFoil : finish === 'etched' ? card.prices.usdEtched : card.prices.usd;
  const n = raw === null ? NaN : Number(raw);
  return Number.isFinite(n) ? n : null;
}

/** Total USD for a set of copies, in cents to avoid float drift, returned in dollars. */
export function totalValue(copies: { card: Pick<Card, 'prices'>; finish: Finish; quantity: number }[]): number {
  let cents = 0;
  for (const c of copies) cents += Math.round((priceFor(c.card, c.finish) ?? 0) * 100) * c.quantity;
  return cents / 100;
}

export const formatUsd = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
